import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Block as BlockIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { BookingFormConfig, EventType, ServiceCategory, TimeSlot, Location } from '../types/bookingTypes';
import { bookingService } from '../services/bookingService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`booking-form-settings-tabpanel-${index}`}
      aria-labelledby={`booking-form-settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const BookingFormSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [config, setConfig] = useState<BookingFormConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'eventType' | 'serviceCategory' | 'timeSlot' | 'location'>('eventType');
  const [editingItem, setEditingItem] = useState<any>(null);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemTime, setNewItemTime] = useState('');
  const [isOnlineOnly, setIsOnlineOnly] = useState(false);
  
  // Off for Today states
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Plan Off states
  const [plannedOffDialogOpen, setPlannedOffDialogOpen] = useState(false);
  const [newPlannedOff, setNewPlannedOff] = useState({
    date: '',
    fromTime: '10:00 AM',
    toTime: '6:00 PM',
    reason: ''
  });

  useEffect(() => {
    loadConfig();
  }, []);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const loadConfig = async () => {
    try {
      let configData = await bookingService.getBookingConfig();
      if (!configData) {
        await bookingService.initializeDefaultConfig();
        configData = await bookingService.getBookingConfig();
      }
      setConfig(configData);
    } catch (error) {
      console.error('Error loading booking config:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to load booking configuration', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await bookingService.updateBookingConfig(config);
      setSnackbar({ 
        open: true, 
        message: 'Booking configuration saved successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error saving booking config:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to save booking configuration', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOffForToday = async () => {
    if (!config) return;

    const confirmMessage = config.offForToday 
      ? "Reopen all time slots for today? Customers will see slots as available based on current time."
      : "Close all time slots for today? Customers will see all today's slots as 'Past' or unavailable.";
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await bookingService.toggleOffForToday();
      // Reload config to get updated status
      const updatedConfig = await bookingService.getBookingConfig();
      if (updatedConfig) {
        setConfig(updatedConfig);
        const statusMessage = updatedConfig.offForToday 
          ? "✅ Office closed for today - all slots appear occupied to customers"
          : "✅ Office reopened for today - slots show normal availability";
        setSnackbar({ open: true, message: statusMessage, severity: 'success' });
      }
    } catch (error) {
      console.error('Error toggling off for today:', error);
      setSnackbar({ open: true, message: 'Failed to toggle office status', severity: 'error' });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const updateFormTexts = (field: string, value: string) => {
    if (!config) return;
    setConfig({
      ...config,
      formTexts: {
        ...config.formTexts,
        [field]: value,
      },
    });
  };

  const updateBusinessSettings = (field: string, value: string | string[] | number) => {
    if (!config) return;
    setConfig({
      ...config,
      businessSettings: {
        ...config.businessSettings,
        [field]: value,
      },
    });
  };

  // Dialog handlers
  const openAddDialog = (type: typeof dialogType) => {
    setDialogType(type);
    setEditingItem(null);
    setNewItemName('');
    setNewItemDescription('');
    setNewItemTime('');
    setIsOnlineOnly(false);
    setDialogOpen(true);
  };

  const openEditDialog = (type: typeof dialogType, item: any) => {
    setDialogType(type);
    setEditingItem(item);
    setNewItemName(item.name || item.time || '');
    setNewItemDescription(item.description || '');
    setNewItemTime(item.time || '');
    setIsOnlineOnly(item.onlineOnly || false);
    setDialogOpen(true);
  };

  const handleDialogSave = () => {
    if (!config) return;

    if (dialogType === 'eventType') {
      if (editingItem) {
        setConfig({
          ...config,
          eventTypes: config.eventTypes.map(item => 
            item.id === editingItem.id ? { ...item, name: newItemName } : item
          ),
        });
        setSnackbar({ 
          open: true, 
          message: 'Event type updated successfully!', 
          severity: 'success' 
        });
      } else {
        const newEventType: EventType = {
          id: `event-${Date.now()}`,
          name: newItemName,
          enabled: true,
          order: config.eventTypes.length + 1,
        };
        setConfig({
          ...config,
          eventTypes: [...config.eventTypes, newEventType],
        });
        setSnackbar({ 
          open: true, 
          message: 'Event type added successfully!', 
          severity: 'success' 
        });
      }
    } else if (dialogType === 'serviceCategory') {
      if (editingItem) {
        setConfig({
          ...config,
          serviceCategories: config.serviceCategories.map(item => 
            item.id === editingItem.id 
              ? { ...item, name: newItemName, description: newItemDescription, onlineOnly: isOnlineOnly }
              : item
          ),
        });
        setSnackbar({ 
          open: true, 
          message: 'Service category updated successfully!', 
          severity: 'success' 
        });
      } else {
        const newCategory: ServiceCategory = {
          id: `category-${Date.now()}`,
          name: newItemName,
          description: newItemDescription,
          enabled: true,
          onlineOnly: isOnlineOnly,
          order: config.serviceCategories.length + 1,
        };
        setConfig({
          ...config,
          serviceCategories: [...config.serviceCategories, newCategory],
        });
        setSnackbar({ 
          open: true, 
          message: 'Service category added successfully!', 
          severity: 'success' 
        });
      }
    } else if (dialogType === 'timeSlot') {
      if (editingItem) {
        setConfig({
          ...config,
          timeSlots: config.timeSlots.map(item => 
            item.id === editingItem.id ? { ...item, time: newItemTime } : item
          ),
        });
        setSnackbar({ 
          open: true, 
          message: 'Time slot updated successfully!', 
          severity: 'success' 
        });
      } else {
        const newTimeSlot: TimeSlot = {
          id: `slot-${Date.now()}`,
          time: newItemTime,
          enabled: true,
          order: config.timeSlots.length + 1,
        };
        setConfig({
          ...config,
          timeSlots: [...config.timeSlots, newTimeSlot],
        });
        setSnackbar({ 
          open: true, 
          message: 'Time slot added successfully!', 
          severity: 'success' 
        });
      }
    } else if (dialogType === 'location') {
      if (editingItem) {
        setConfig({
          ...config,
          locations: config.locations.map(item => 
            item.id === editingItem.id ? { ...item, name: newItemName } : item
          ),
        });
        setSnackbar({ 
          open: true, 
          message: 'Location updated successfully!', 
          severity: 'success' 
        });
      } else {
        const newLocation: Location = {
          id: `loc-${Date.now()}`,
          name: newItemName,
          enabled: true,
          order: config.locations.length + 1,
        };
        setConfig({
          ...config,
          locations: [...config.locations, newLocation],
        });
        setSnackbar({ 
          open: true, 
          message: 'Location added successfully!', 
          severity: 'success' 
        });
      }
    }

    setDialogOpen(false);
  };

  const handleDelete = (type: typeof dialogType, id: string) => {
    if (!config) return;
    
    if (type === 'eventType') {
      setConfig({
        ...config,
        eventTypes: config.eventTypes.filter(item => item.id !== id),
      });
      setSnackbar({ 
        open: true, 
        message: 'Event type deleted successfully!', 
        severity: 'success' 
      });
    } else if (type === 'serviceCategory') {
      setConfig({
        ...config,
        serviceCategories: config.serviceCategories.filter(item => item.id !== id),
      });
      setSnackbar({ 
        open: true, 
        message: 'Service category deleted successfully!', 
        severity: 'success' 
      });
    } else if (type === 'timeSlot') {
      setConfig({
        ...config,
        timeSlots: config.timeSlots.filter(item => item.id !== id),
      });
      setSnackbar({ 
        open: true, 
        message: 'Time slot deleted successfully!', 
        severity: 'success' 
      });
    } else if (type === 'location') {
      setConfig({
        ...config,
        locations: config.locations.filter(item => item.id !== id),
      });
      setSnackbar({ 
        open: true, 
        message: 'Location deleted successfully!', 
        severity: 'success' 
      });
    }
  };

  const toggleEnabled = (type: typeof dialogType, id: string) => {
    if (!config) return;
    
    if (type === 'eventType') {
      setConfig({
        ...config,
        eventTypes: config.eventTypes.map(item => 
          item.id === id ? { ...item, enabled: !item.enabled } : item
        ),
      });
    } else if (type === 'serviceCategory') {
      setConfig({
        ...config,
        serviceCategories: config.serviceCategories.map(item => 
          item.id === id ? { ...item, enabled: !item.enabled } : item
        ),
      });
    } else if (type === 'timeSlot') {
      setConfig({
        ...config,
        timeSlots: config.timeSlots.map(item => 
          item.id === id ? { ...item, enabled: !item.enabled } : item
        ),
      });
    } else if (type === 'location') {
      setConfig({
        ...config,
        locations: config.locations.map(item => 
          item.id === id ? { ...item, enabled: !item.enabled } : item
        ),
      });
    }
  };

  // Planned Off handlers
  const handleAddPlannedOff = () => {
    if (!config || !newPlannedOff.date || !newPlannedOff.fromTime || !newPlannedOff.toTime) return;

    const newEntry = {
      id: `planned_${Date.now()}`,
      date: newPlannedOff.date,
      fromTime: newPlannedOff.fromTime,
      toTime: newPlannedOff.toTime,
      reason: newPlannedOff.reason || '',
      enabled: true
    };

    setConfig({
      ...config,
      plannedOff: [...(config.plannedOff || []), newEntry]
    });

    setNewPlannedOff({
      date: '',
      fromTime: '10:00 AM',
      toTime: '6:00 PM',
      reason: ''
    });
    setPlannedOffDialogOpen(false);
  };

  const handleDeletePlannedOff = (id: string) => {
    if (!config) return;
    setConfig({
      ...config,
      plannedOff: (config.plannedOff || []).filter(item => item.id !== id)
    });
  };

  const handleTogglePlannedOff = (id: string) => {
    if (!config) return;
    setConfig({
      ...config,
      plannedOff: (config.plannedOff || []).map(item => 
        item.id === id ? { ...item, enabled: !item.enabled } : item
      )
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading booking form settings...</Typography>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Failed to load booking configuration</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Booking Form Settings
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant={config?.offForToday ? "contained" : "outlined"}
            color={config?.offForToday ? "warning" : "primary"}
            onClick={handleToggleOffForToday}
            startIcon={config?.offForToday ? <BlockIcon /> : <ScheduleIcon />}
            sx={{ minWidth: 180 }}
          >
            {config?.offForToday ? 'Slots Closed Today' : 'Close Slots for Today'}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveConfig}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {/* Off for Today Status Info */}
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Typography variant="caption" color="text.secondary">
          Current Time: {currentTime.toLocaleString('en-MY', { 
            timeZone: config?.timezone || 'Asia/Kuala_Lumpur',
            dateStyle: 'medium',
            timeStyle: 'short'
          })}
        </Typography>
        {config?.offForToday && (
          <Chip 
            label="Customers see today's slots as 'Past/Unavailable'" 
            size="small" 
            color="warning" 
            variant="outlined" 
          />
        )}
      </Stack>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Event Types" />
            <Tab label="Service Categories" />
            <Tab label="Time Slots" />
            <Tab label="Locations" />
            <Tab label="Form Text" />
            <Tab label="Business Settings" />
            <Tab label="Plan Off" />
          </Tabs>
        </Box>

        {/* Event Types Tab */}
        <TabPanel value={tabValue} index={0}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Event Types</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => openAddDialog('eventType')}
            >
              Add Event Type
            </Button>
          </Stack>
          <List>
            {config.eventTypes.map((eventType) => (
              <ListItem key={eventType.id} divider>
                <ListItemText primary={eventType.name} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={eventType.enabled}
                        onChange={() => toggleEnabled('eventType', eventType.id)}
                      />
                    }
                    label="Enabled"
                  />
                  <IconButton onClick={() => openEditDialog('eventType', eventType)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete('eventType', eventType.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Service Categories Tab */}
        <TabPanel value={tabValue} index={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Service Categories</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => openAddDialog('serviceCategory')}
            >
              Add Category
            </Button>
          </Stack>
          <List>
            {config.serviceCategories.map((category) => (
              <ListItem key={category.id} divider>
                <ListItemText 
                  primary={category.name}
                  secondary={category.description}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  {category.onlineOnly && (
                    <Chip label="Online Only" size="small" color="secondary" />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={category.enabled}
                        onChange={() => toggleEnabled('serviceCategory', category.id)}
                      />
                    }
                    label="Enabled"
                  />
                  <IconButton onClick={() => openEditDialog('serviceCategory', category)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete('serviceCategory', category.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Time Slots Tab */}
        <TabPanel value={tabValue} index={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Time Slots</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => openAddDialog('timeSlot')}
            >
              Add Time Slot
            </Button>
          </Stack>
          <List>
            {config.timeSlots.map((timeSlot) => (
              <ListItem key={timeSlot.id} divider>
                <ListItemText primary={timeSlot.time} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={timeSlot.enabled}
                        onChange={() => toggleEnabled('timeSlot', timeSlot.id)}
                      />
                    }
                    label="Enabled"
                  />
                  <IconButton onClick={() => openEditDialog('timeSlot', timeSlot)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete('timeSlot', timeSlot.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Locations Tab */}
        <TabPanel value={tabValue} index={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Locations</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => openAddDialog('location')}
            >
              Add Location
            </Button>
          </Stack>
          <List>
            {config.locations.map((location) => (
              <ListItem key={location.id} divider>
                <ListItemText primary={location.name} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={location.enabled}
                        onChange={() => toggleEnabled('location', location.id)}
                      />
                    }
                    label="Enabled"
                  />
                  <IconButton onClick={() => openEditDialog('location', location)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete('location', location.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Form Text Tab */}
        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" mb={2}>Form Text Configuration</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Modal Title"
                value={config.formTexts.modalTitle}
                onChange={(e) => updateFormTexts('modalTitle', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Modal Subtitle"
                value={config.formTexts.modalSubtitle}
                onChange={(e) => updateFormTexts('modalSubtitle', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Step 1 Title"
                value={config.formTexts.step1Title}
                onChange={(e) => updateFormTexts('step1Title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Step 2 Title"
                value={config.formTexts.step2Title}
                onChange={(e) => updateFormTexts('step2Title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Step 3 Title"
                value={config.formTexts.step3Title}
                onChange={(e) => updateFormTexts('step3Title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Step 4 Title"
                value={config.formTexts.step4Title}
                onChange={(e) => updateFormTexts('step4Title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Submit Button Text"
                value={config.formTexts.submitButtonText}
                onChange={(e) => updateFormTexts('submitButtonText', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cancel Button Text"
                value={config.formTexts.cancelButtonText}
                onChange={(e) => updateFormTexts('cancelButtonText', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Success Message"
                value={config.formTexts.successMessage}
                onChange={(e) => updateFormTexts('successMessage', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Error Message"
                value={config.formTexts.errorMessage}
                onChange={(e) => updateFormTexts('errorMessage', e.target.value)}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Business Settings Tab */}
        <TabPanel value={tabValue} index={5}>
          <Typography variant="h6" mb={2}>Business Settings</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Name"
                value={config.businessSettings.businessName}
                onChange={(e) => updateBusinessSettings('businessName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={config.businessSettings.contactEmail}
                onChange={(e) => updateBusinessSettings('contactEmail', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={config.businessSettings.contactPhone}
                onChange={(e) => updateBusinessSettings('contactPhone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Working Days (comma separated)"
                value={config.businessSettings.workingDays.join(', ')}
                onChange={(e) => updateBusinessSettings('workingDays', e.target.value.split(', '))}
                helperText="e.g., Monday, Tuesday, Wednesday"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Booking Days"
                value={config.businessSettings.minBookingDays}
                onChange={(e) => updateBusinessSettings('minBookingDays', parseInt(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Maximum Booking Days"
                value={config.businessSettings.maxBookingDays}
                onChange={(e) => updateBusinessSettings('maxBookingDays', parseInt(e.target.value))}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Plan Off Tab */}
        <TabPanel value={tabValue} index={6}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Planned Off Schedule</Typography>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setPlannedOffDialogOpen(true)}
            >
              Add Planned Off
            </Button>
          </Box>
          
          {config?.plannedOff?.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No planned off dates scheduled. Click "Add Planned Off" to create one.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Time Range</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {config?.plannedOff
                    ?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map((plannedOff) => (
                    <TableRow key={plannedOff.id}>
                      <TableCell>
                        {new Date(plannedOff.date).toLocaleDateString('en-MY', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </TableCell>
                      <TableCell>{plannedOff.fromTime} - {plannedOff.toTime}</TableCell>
                      <TableCell>{plannedOff.reason || '-'}</TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={plannedOff.enabled}
                              onChange={() => handleTogglePlannedOff(plannedOff.id)}
                              size="small"
                            />
                          }
                          label={plannedOff.enabled ? 'Active' : 'Disabled'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeletePlannedOff(plannedOff.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit' : 'Add'} {dialogType === 'eventType' ? 'Event Type' : 
           dialogType === 'serviceCategory' ? 'Service Category' :
           dialogType === 'timeSlot' ? 'Time Slot' : 'Location'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogType === 'timeSlot' ? (
              <TextField
                fullWidth
                label="Time"
                value={newItemTime}
                onChange={(e) => setNewItemTime(e.target.value)}
                placeholder="e.g., 10:00 AM"
              />
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Name"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  sx={{ mb: 2 }}
                />
                {dialogType === 'serviceCategory' && (
                  <>
                    <TextField
                      fullWidth
                      label="Description"
                      value={newItemDescription}
                      onChange={(e) => setNewItemDescription(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isOnlineOnly}
                          onChange={(e) => setIsOnlineOnly(e.target.checked)}
                        />
                      }
                      label="Online Only"
                    />
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDialogSave} variant="contained">
            {editingItem ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Planned Off Dialog */}
      <Dialog open={plannedOffDialogOpen} onClose={() => setPlannedOffDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Planned Off Schedule</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Date"
              type="date"
              value={newPlannedOff.date}
              onChange={(e) => setNewPlannedOff({ ...newPlannedOff, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              inputProps={{
                min: new Date().toISOString().split('T')[0] // Can't select past dates
              }}
            />
            
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>From Time</InputLabel>
                <Select
                  value={newPlannedOff.fromTime}
                  onChange={(e) => setNewPlannedOff({ ...newPlannedOff, fromTime: e.target.value })}
                  label="From Time"
                >
                  {config?.timeSlots?.map((slot) => (
                    <MenuItem key={`from-${slot.id}`} value={slot.time}>
                      {slot.time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel>To Time</InputLabel>
                <Select
                  value={newPlannedOff.toTime}
                  onChange={(e) => setNewPlannedOff({ ...newPlannedOff, toTime: e.target.value })}
                  label="To Time"
                >
                  {config?.timeSlots?.map((slot) => (
                    <MenuItem key={`to-${slot.id}`} value={slot.time}>
                      {slot.time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <TextField
              label="Reason (Optional)"
              value={newPlannedOff.reason}
              onChange={(e) => setNewPlannedOff({ ...newPlannedOff, reason: e.target.value })}
              placeholder="e.g., Meeting, Personal leave, Maintenance"
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlannedOffDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddPlannedOff} 
            variant="contained"
            disabled={!newPlannedOff.date || !newPlannedOff.fromTime || !newPlannedOff.toTime}
          >
            Add Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingFormSettings; 